// Roles — ONE set of names, exactly as they are stored in rs-roles (lowercase). There is no second naming system and no
// conversion between systems: the route guards, the billing check and the server all use these same strings.
export type Role = 'admin' | 'owner' | 'manager' | 'salesManager' | 'financeManager' | 'lead' | 'staff' | 'client';

// Groups of roles, for guards written as `allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES]}`
export const OWNER_ROLES: Role[] = ['admin', 'owner'];
export const MANAGER_ROLES: Role[] = ['manager', 'salesManager', 'financeManager'];
export const STAFF_ROLES: Role[] = ['lead', 'staff'];
export const CLIENT_ROLES: Role[] = ['client'];

// Who may use the customer Inbox (every customer chat, text and email): owners and managers only. Lead and staff have the team
// chat instead; a client sees only their own conversation with the business. The Firestore rules for the chat database repeat
// this list (firestore.wix-chat.rules), so change both together.
export const INBOX_ROLES: Role[] = [...OWNER_ROLES, ...MANAGER_ROLES];

// Who may use the phone at all (the softphone, being a phone agent, texting from it): a lead and above. Staff and clients get none
// of it. The voice functions repeat this list (functions-voice/src/utils/phoneAccess.ts: that codebase does not ship the shared
// package), so change both together.
export const PHONE_ROLES: Role[] = [...OWNER_ROLES, ...MANAGER_ROLES, 'lead'];

// Who may see the customer contact list and the financial data kept with it (billing, labels, payments, subscriptions): owners and
// managers only. The Firestore rules for the contacts database repeat this list (firestore.contacts.rules), so change both together.
export const CONTACT_ROLES: Role[] = [...OWNER_ROLES, ...MANAGER_ROLES];

// A lead also reads the contact list, but the device keeps only each contact's name, phone number and photo from it (so the
// softphone can show who a number belongs to). Same repeat of this list in firestore.contacts.rules.
export const CONTACT_NAME_ROLES: Role[] = [...CONTACT_ROLES, 'lead'];


export const isOwnerRole = (role?: string) => OWNER_ROLES.includes(role as Role);
export const isManagerRole = (role?: string) => MANAGER_ROLES.includes(role as Role);
export const isStaffRole = (role?: string) => STAFF_ROLES.includes(role as Role);

/**
 * Who may hand out which role: owners/admins any role; managers "staff" and "lead" only; everyone else none.
 * (The server enforces this; the dropdowns use the same function to offer only what will be accepted.)
 */
export function canAssignRole(callerRole: string | undefined, newRole: string): boolean {
  if (isOwnerRole(callerRole)) return true;
  if (isManagerRole(callerRole)) return isStaffRole(newRole);
  return false;
}

/** Who may manage (change the role of / notifications for) an existing account: owners/admins any; managers staff and lead accounts only. */
export function canManageAccount(callerRole: string | undefined, targetRole: string | undefined): boolean {
  if (isOwnerRole(callerRole)) return true;
  if (isManagerRole(callerRole)) return isStaffRole(targetRole);
  return false;
}

export interface User {
  id: string;
  email: string;
  role: Role;
  isActive: boolean;
}
